// @flow weak
import React from 'react';
import PropTypes from 'prop-types';
import { DragSource, DropTarget } from 'react-dnd';
import { get, isArray, isNumber, isObject, isString, map, without } from 'lodash';
import moment from 'moment';
import TableCell from '@material-ui/core/TableCell';
import Icon from '@material-ui/core/Icon';
import LinearProgress from '@material-ui/core/LinearProgress';
import { parseDate } from '@lib/services/datesService';
import { muiThemeLuma } from 'themes';

const ROW = 'row';

const styles = {
  moveButton: {
    cursor: 'move',
    marginTop: 12,
    zIndex: 2,
  },
};

// Implements the drag source contract.
const rowSource = {
  beginDrag: (props) => {
    const { id } = props;

    if (props.onBeginDrag && !props.isDragging) props.onBeginDrag(id);

    return { id };
  },
  endDrag: (props, monitor) => {
    if (props.onEndDrag) {
      props.onEndDrag(monitor.getItem().id, monitor.getDropResult());
    }
  },
};

// Implements the drag target contract.
const rowTarget = {
  hover: (props, monitor) => {
    const { id, onMove } = props;
    const draggedId = monitor.getItem().id;

    if (draggedId !== id) onMove(draggedId, id);
  },
};

// Specifies the props to inject into your component.
function collect(connect, monitor) {
  return {
    connectDragSource: connect.dragSource(),
    connectDragPreview: connect.dragPreview(),
    isDragging: monitor.isDragging(),
  };
}

// Specifies the props to inject into your component.
function collectTarget(connect) {
  return {
    connectDropTarget: connect.dropTarget(),
  };
}

/**
 * A material table row.
 *
 * Will automatically set dynamic row height
 * based on the material table element parent (head, body, etc)
 *
 * ```jsx
 * <EnhancedTableRow>
 *   <TableCell>....</TableCell>
 * </EnhancedTableRow>
 * ```
 */
class EnhancedTableRow extends React.PureComponent {
  getItemValue(item, columnData) {
    if (!item) {
      return;
    }
    const id = get(columnData, 'id');
    // use callback if any
    if (columnData.callback) {
      return columnData.callback(item, id);
    }
    let value = get(item, id);

    // date
    if (columnData.date) {
      if (!value) {
        return '-';
      }
      if (isString(columnData.date)) {
        // TODO unify ways to display DOB and other dates
        if (isObject(value)) {
          return parseDate(value, columnData.date);
        }
        return moment(value).format(columnData.date);
      }
      return moment(value).fromNow();
    }
    // array
    if (isArray(value)) {
      return columnData.numeric ? value.length : map(value, 'name').join(', ');
    }
    // object
    if (isObject(value)) {
      return get(value, 'name', '-');
    }
    // number
    if (isNumber(value)) {
      value = value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    return value;
  }

  renderMoveWaitlistButton() {
    const { onEndDrag, connectDragSource } = this.props;

    if (!onEndDrag) return;

    return connectDragSource(
      <div>
        <Icon style={styles.moveButton}>swap_vert</Icon>
      </div>,
    );
  }

  renderTableCell(item, column, index) {
    const { columnStyle, renderItem } = this.props;

    // TODO(danielmana): move to theme
    const style = {
      maxWidth: 200,
      // first column with bold
      fontWeight: index === 0 ? 600 : 400,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontFamily: muiThemeLuma.typography.fontFamily,
      fontSize: 12,
    };

    if (column.id === 'actions') {
      const { renderActions } = this.props;
      return (
        <TableCell key={column.id} style={{ ...style, padding: '0 0 0 24px' }} align="right">
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {this.renderMoveWaitlistButton()}
            {renderActions(item)}
          </div>
        </TableCell>
      );
    }

    const value = this.getItemValue(item, column);

    return (
      <TableCell
        key={column.id}
        style={columnStyle && columnStyle[column.id] ? columnStyle[column.id] : style}
        padding={column.padding || 'dense'}
        align={column.numeric ? 'right' : 'inherit'}
      >
        {renderItem ? renderItem(column, item, value) : this.renderValue(column, value)}
        {this.renderValueProgress(column, value)}
      </TableCell>
    );
  }

  renderValue(column, value) {
    if (column.isHTML) {
      return (
        <div dangerouslySetInnerHTML={{ __html: value }} /> // eslint-disable-line react/no-danger
      );
    }
    if (column.showProgress) {
      return value.label;
    }
    return value;
  }

  renderValueProgress(column, value) {
    if (!column.showProgress) {
      return;
    }
    return <LinearProgress variant="determinate" value={value.progress} />;
  }

  renderRow() {
    const { children, isDragging, columnData, onClick, item, ...custom } = this.props;

    const style = {
      cursor: onClick ? 'pointer' : 'default',
      height: 40,
      opacity: isDragging ? 0 : 1,
      transform: 'translateZ(0)',
    };

    // skip props for drag&drop
    const other = without(
      custom,
      'className',
      'hover',
      'selected',
      'dragging',
      'draggingMode',
      'connectDragSource',
      'connectDropTarget',
      'connectDragPreview',
      'onMove',
      'onBeginDrag',
      'onEndDrag',
      'isDragging',
      'index',
    );

    return (
      <tr {...other} onClick={onClick} style={style}>
        {children}
        {map(columnData, (column, index) => this.renderTableCell(item, column, index))}
      </tr>
    );
  }

  render() {
    const { connectDropTarget, connectDragPreview, onEndDrag } = this.props;

    if (onEndDrag) return connectDragPreview(connectDropTarget(this.renderRow()));

    return this.renderRow();
  }
}

EnhancedTableRow.propTypes = {
  /**
   * Should be valid `<tr>` children such as `TableCell`.
   */
  children: PropTypes.node,
  /**
   * The CSS class name of the root element.
   */
  className: PropTypes.string,
  /**
   * If set to true, the table row will shade on hover.
   */
  hover: PropTypes.bool,
  /**
   * If set to true, the table row will have the selected shading.
   */
  selected: PropTypes.bool,
  isDragging: PropTypes.bool,
  dragging: PropTypes.bool,
  draggingMode: PropTypes.bool,
  onEndDrag: PropTypes.func,
  connectDragPreview: PropTypes.func,
  connectDropTarget: PropTypes.func,
  connectDragSource: PropTypes.func,
  renderActions: PropTypes.func,
  columnData: PropTypes.array,
  columnStyle: PropTypes.object,
  item: PropTypes.object,
  onClick: PropTypes.func,
  renderItem: PropTypes.func,
  expanded: PropTypes.any,
};

EnhancedTableRow.defaultProps = {
  hover: false,
  selected: false,
};

EnhancedTableRow.contextTypes = {
  table: PropTypes.object,
};

export default DragSource(ROW, rowSource, collect)(DropTarget(ROW, rowTarget, collectTarget)(EnhancedTableRow));
